import { DEFAULT_TYPOGRAPHY, ResolvedTypography, TypographySettings } from "@core/data/types/advanced-report";

/**
 * Drops keys whose value is `undefined` so they cannot clobber an inherited parent value
 * during the merge — `Object.assign` otherwise overwrites with an explicit `undefined`.
 */
export function stripUndefined<T extends object>(source: T | undefined): Partial<T> {
    if (!source) return {};

    const out = {} as Partial<T>;
    (Object.keys(source) as Array<keyof T>).forEach(key => {
        if (source[key] !== undefined) out[key] = source[key];
    });
    return out;
}

/**
 * Resolves an inheritable setting down a Report → Page → Component chain by merging each tier's
 * defined leaves over the previous, starting from `defaults`. Later (more specific) tiers win
 * per-leaf, so a tier that sets only some fields inherits the rest from its parents.
 */
export function resolveInherited<T extends object>(chain: Array<Partial<T> | undefined>, defaults: T): T {
    return Object.assign({}, defaults, ...chain.map(stripUndefined));
}

export function resolveTypography(chain: Array<TypographySettings | undefined>): ResolvedTypography {
    return resolveInherited<ResolvedTypography>(chain as Array<Partial<ResolvedTypography>>, DEFAULT_TYPOGRAPHY);
}
